.page {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page.fullscreen {
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-modal);
  background: var(--color-bg-deep);
}

.page.fullscreen .toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  border-radius: 0;
  border-bottom: 1px solid var(--color-border);
}

.page.fullscreen .mapContainer {
  flex: 1;
}

.page.fullscreen .instructions,
.page.fullscreen .fallbackInfo {
  display: none;
}

/* Toolbar */
.toolbar {
  margin-bottom: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--color-border);
}

.toolbarLeft {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.title {
  font-family: var(--font-heading);
  font-size: 20px;
  font-weight: 400;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.status {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--color-bg-input);
  color: var(--color-text-dim);
}

.status.loaded {
  background: rgba(90, 166, 66, 0.15);
  color: var(--color-online);
  border: 1px solid rgba(90, 166, 66, 0.3);
}

.status.error {
  background: rgba(255, 85, 85, 0.15);
  color: var(--color-red);
  border: 1px solid rgba(255, 85, 85, 0.3);
}

.toolbarRight {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* Map Container */
.mapContainer {
  flex: 1;
  position: relative;
  width: 100%;
  background: var(--color-bg-deep);
}

.iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: var(--color-bg-deep);
  transition: opacity var(--transition-base);
}

.iframe.loading {
  opacity: 0;
}

.iframe.error {
  display: none;
}

/* Loading Overlay */
.loadingOverlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  background: var(--color-bg-deep);
  z-index: 5;
  color: var(--color-text-dim);
}

.spinner {
  width: 48px;
  height: 48px;
  color: var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Fallback */
.fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: var(--space-8);
  text-align: center;
  color: var(--color-text-dim);
  gap: var(--space-6);
}

.illustration {
  width: 300px;
  height: 300px;
  max-width: 80vw;
  opacity: 0.6;
}

.fallback h2 {
  font-family: var(--font-heading);
  font-size: 28px;
  font-weight: 400;
  color: var(--color-text);
}

.fallback p {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.6;
  max-width: 400px;
}

.fallbackInfo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-6);
  background: var(--color-bg-panel);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  text-align: left;
  max-width: 400px;
  width: 100%;
}

.fallbackInfo h3 {
  font-family: var(--font-heading);
  font-size: 16px;
  font-weight: 400;
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border-dim);
  padding-bottom: var(--space-2);
  margin-bottom: var(--space-2);
}

.fallbackInfo ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-2);
}

.fallbackInfo li {
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--color-text-dim);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.fallbackInfo li::before {
  content: '✓';
  color: var(--color-primary);
  font-weight: 700;
}

/* Instructions */
.instructions {
  margin-top: var(--space-6);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.instructions h3 {
  font-family: var(--font-heading);
  font-size: 16px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-4);
}

.controlsGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-3);
}

@media (min-width: 768px) {
  .controlsGrid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .controlsGrid {
    grid-template-columns: repeat(6, 1fr);
  }
}

.controlItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-bg-input);
  border: 1px solid var(--color-border-dim);
  border-radius: var(--radius-md);
  text-align: center;
  transition: all var(--transition-fast);
}

.controlItem:hover {
  border-color: var(--color-primary);
  background: var(--color-bg-panel);
}

.keys {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-1);
}

.keys kbd {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--color-text);
  background: var(--color-bg-deep);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-1) var(--space-2);
  white-space: nowrap;
}

.keySeparator {
  font-family: var(--font-body);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-muted);
  margin: 0 var(--space-1);
}

.action {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--color-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Responsive */
@media (max-width: 767px) {
  .page {
    height: auto;
    min-height: 100vh;
  }

  .mapContainer {
    height: 60vh;
    min-height: 400px;
  }

  .toolbarLeft {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .title {
    font-size: 18px;
  }

  .toolbarRight {
    width: 100%;
    justify-content: stretch;
  }

  .toolbarRight :global(.mc-button) {
    flex: 1;
  }
}